import React from "react";
import { useInView } from "react-intersection-observer";

const ServiceCard = ({ num, icon, name, desc, tags }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  return (
    <div
      ref={ref}
      className={`service-card ${inView ? "visible" : ""}`}
      style={{
        background: "var(--surface)",
        padding: "2rem 1.5rem",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "1.5rem",
          right: "1.5rem",
          fontSize: "2.5rem",
          fontWeight: 900,
          color: "var(--light)",
          lineHeight: 1,
        }}
      >
        {num}
      </div>
      <div
        style={{ marginBottom: "1.5rem" }}
        dangerouslySetInnerHTML={{ __html: icon }}
      />
      <div
        className="service-name"
        style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.8rem" }}
      >
        {name}
      </div>
      <p
        className="service-desc-text"
        style={{ fontSize: "0.88rem", color: "var(--muted)", lineHeight: 1.7 }}
      >
        {desc}
      </p>
      <div
        className="service-tags"
        style={{
          marginTop: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "0.4rem",
        }}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              border: "1px solid var(--border)",
              color: "var(--muted)",
              padding: "0.25rem 0.7rem",
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

const Services = () => {
  const servicesData = [
    {
      num: "01",
      name: "Software Development",
      desc: "From a professional business website to a fully custom enterprise web application, we design and build digital solutions that are scalable, secure, and built to perform. Whether you need a CMS your team can manage, an e-commerce platform built to convert, or a complex API-driven system - we engineer it right, the first time.",
      tags: ["Websites", "Enterprise Apps", "CMS", "e-Commerce", "APIs"],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F57C00" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    },
    {
      num: "02",
      name: "Search Engine Optimisation",
      desc: "Being found on Google is not luck - it is strategy. Our SEO specialists combine technical precision with content authority to improve your search rankings, attract qualified traffic, and convert online visibility into consistent business growth.",
      tags: ["Technical SEO", "Content", "Link Building", "Audits"],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1565C0" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>',
    },
    {
      num: "03",
      name: "Data Analysis & BI",
      desc: "Your business generates valuable data every day - we help you harness it. We build intuitive dashboards, automated reports, and analytical models that give your leadership team the clarity and confidence to make faster, better-informed decisions.",
      tags: ["Dashboards", "BI Reports", "Analytics", "Forecasting"],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F57C00" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>',
    },
    {
      num: "04",
      name: "Business Consulting",
      desc: "We work alongside founders, executives, and business leaders to identify inefficiencies, streamline operations, and build the processes that support sustainable growth. From project delivery to process re-engineering, we bring structure and clarity to your most complex business challenges.",
      tags: [
        "Process Engineering",
        "Project Management",
        "Business Improvement",
      ],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ec4d37" stroke-width="2"><rect x="3" y="7" width="18" height="13" rx="2" ry="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>',
    },
    {
      num: "05",
      name: "Software Quality Assurance",
      desc: "Software quality is a business requirement, not an afterthought. Our QA engineers apply structured testing methodologies - automated and manual - across your entire application to identify defects early, reduce risk, and ensure every release meets the highest standard of reliability.",
      tags: ["Automation", "Manual QA", "Performance", "Security", "CI/CD"],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c49000" stroke-width="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>',
    },
    {
      num: "06",
      name: "Bulk Notifications",
      desc: "With an open rate of up to 98%, SMS remains the most direct and cost-effective channel to reach your customers. We manage your bulk messaging campaigns end-to-end - from audience segmentation and message personalisation to scheduling, delivery, and real-time performance reporting.",
      tags: ["SMS Marketing", "Campaigns", "Delivery Reports", "Bulk SMS"],
      icon: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F57C00" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>',
    },
  ];

  const topThree = servicesData;
  // const bottomTwo = servicesData.slice(3);

  const { ref: headerRef, inView: headerInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const { ref: gridRef, inView: gridInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section id="services" style={{ background: "var(--surface)" }}>
      <div className="services-header" style={{ marginBottom: "2rem" }}>
        <div ref={headerRef}>
          <div className={`section-tag ${headerInView ? "visible" : ""}`}>
            <span className="section-tag-icon"></span> What We Offer
          </div>
          <h2 className={`section-title ${headerInView ? "visible" : ""}`}>
            Our <em>Services</em>
          </h2>
        </div>
      </div>
      <div
        className={`services-grid ${gridInView ? "visible" : ""}`}
        ref={gridRef}
      >
        {topThree.map((s) => (
          <ServiceCard key={s.num} {...s} />
        ))}
      </div>
      {/* <div className="services-grid-bottom" style={{ marginTop: "1.5px" }}>
        {bottomTwo.map((s) => (
          <ServiceCard key={s.num} {...s} />
        ))}
      </div> */}
    </section>
  );
};

export default Services;
